import { useMemo, useState } from 'react';
import type { Location, Role, Staff } from '@salonomy/types';
import { Button, Card, Pill, Select } from '@salonomy/ui';
import { useUpdateStaff } from '../../data/staff.js';
import { ROLE_LABEL } from '../../lib/rbac.js';
import { useToast } from '../../lib/toast.js';
import { LocationModal } from './LocationModal.js';

/**
 * Locations editor (WS6 item 4). Lists the salon's sites, lets a manager add/edit each, and assign
 * staff to a location inline (mirrors StaffModal's location field, so a manager can reorganise the
 * floor without opening every team member). Manager+ only — the host only mounts this for managers,
 * but the underlying mutations re-check on the server.
 *
 * Data comes in as props (`locations`, `staff`) so this stays a pure render over what SettingsPage
 * already loads — same idiom as RotaEditor and StaffModal. The only write hooks it owns are the
 * location create/edit (its own modal) and staff-location reassignment.
 */
export function LocationsPanel({ locations, staff }: { locations: Location[]; staff: Staff[] }) {
  const toast = useToast();
  const updateStaff = useUpdateStaff();
  const [modal, setModal] = useState<{ existing?: Location } | null>(null);
  // The staff row currently mid-reassignment, so we can disable just that Select while it saves.
  const [movingStaffId, setMovingStaffId] = useState<string | null>(null);

  // Active staff only — deactivated members shouldn't clutter the floor plan. Sorted by name.
  const assignable = useMemo(
    () => staff.filter((s) => s.active).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [staff],
  );

  // Staff grouped by their locationId; `null` bucket = "not assigned to a site".
  const byLocation = useMemo(() => {
    const map = new Map<string | null, Staff[]>();
    for (const s of assignable) {
      const key = s.locationId ?? null;
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
    return map;
  }, [assignable]);

  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => a.name.localeCompare(b.name)),
    [locations],
  );

  async function reassign(member: Staff, nextLocationId: string) {
    const target = nextLocationId || null;
    if (target === (member.locationId ?? null)) return;
    setMovingStaffId(member.id);
    try {
      await updateStaff.mutateAsync({ id: member.id, body: { locationId: target } });
      const dest = target ? locations.find((l) => l.id === target)?.name : null;
      toast.success(
        dest ? `${member.displayName} moved to ${dest}` : `${member.displayName} unassigned`,
      );
    } catch {
      toast.error('Could not reassign this person.');
    } finally {
      setMovingStaffId(null);
    }
  }

  const unassigned = byLocation.get(null) ?? [];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div className="section-title">Locations</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Your salon’s sites. Set an address and assign each team member to where they work.
          </div>
        </div>
        <Button size="sm" onClick={() => setModal({})}>
          Add location
        </Button>
      </div>

      {sortedLocations.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '14px 0 0' }}>
          No locations yet. Add one to give the salon an address and group your team by site.
        </div>
      ) : (
        <div className="settings-list" style={{ marginTop: 8 }}>
          {sortedLocations.map((l) => {
            const here = byLocation.get(l.id) ?? [];
            return (
              <div key={l.id} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>{l.name}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {formatAddress(l) ?? 'No address set'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Pill tone="neutral">
                    {here.length} {here.length === 1 ? 'person' : 'people'}
                  </Pill>
                  <Button size="sm" variant="ghost" onClick={() => setModal({ existing: l })}>
                    Edit
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Inline staff assignment — only meaningful once there's at least one site to assign to. */}
      {sortedLocations.length > 0 && assignable.length > 0 ? (
        <>
          <div className="section-title" style={{ fontSize: 15, margin: '16px 0 2px' }}>
            Team by location
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
            Reassign a team member’s site here, or from their profile in Team.
          </div>
          <div className="settings-list">
            {assignable.map((s) => (
              <div key={s.id} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>{s.displayName}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {s.title ?? roleLabel(s.role)}
                  </div>
                </div>
                <Select
                  value={s.locationId ?? ''}
                  disabled={movingStaffId === s.id}
                  onChange={(e) => reassign(s, e.target.value)}
                  style={{ maxWidth: 220 }}
                >
                  <option value="">No specific location</option>
                  {sortedLocations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
          {unassigned.length > 0 ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              {unassigned.length} not yet assigned to a site.
            </div>
          ) : null}
        </>
      ) : null}

      {modal ? <LocationModal existing={modal.existing} onClose={() => setModal(null)} /> : null}
    </Card>
  );
}

function roleLabel(role: Role): string {
  return ROLE_LABEL[role] ?? role;
}

/** "12 Carnaby Street, London W1F 9PS" from whatever address parts are present, or null if none. */
function formatAddress(l: Location): string | null {
  const street = [l.addressLine1, l.addressLine2].filter(Boolean).join(', ');
  const town = [l.city, l.postcode].filter(Boolean).join(' ');
  const full = [street, town].filter(Boolean).join(', ');
  return full || null;
}
